/**
 * Temperature formatting utility — locale-aware with user override.
 *
 * Background: the weather API (Open-Meteo) returns Celsius; UI converts
 * for display. Users outside the US (and a handful of other countries)
 * expect Celsius by default. Shipping only Fahrenheit breaks that.
 *
 * Three-tier precedence (highest wins):
 *   1. Explicit user preference  ('C' | 'F')  — from settings toggle
 *   2. Locale-derived default    — US/BS/BZ/KY/LR/MM/PR default to F
 *   3. Fallback default          — 'C' (most of the world)
 *
 * Countries that primarily use Fahrenheit:
 *   United States, Bahamas, Belize, Cayman Islands, Liberia, Myanmar,
 *   Marshall Islands, Federated States of Micronesia, Palau, Puerto Rico.
 *
 * Usage:
 *   import { formatTemperature, resolveTemperatureUnit } from '.../temperatureFormat';
 *   const unit = resolveTemperatureUnit({ userPref: null, locale: 'en-US' });
 *   // => 'F'
 *   formatTemperature(8, { unit });       // => '46°F'
 *   formatTemperature(8, { unit: 'C' });  // => '8°C'
 *   formatTemperature(8, { unit: 'F', suffix: '°' });  // => '46°'
 */

// Fahrenheit-default countries — ISO 3166-1 alpha-2
const FAHRENHEIT_LOCALES = new Set([
  'us', // United States
  'bs', // Bahamas
  'bz', // Belize
  'ky', // Cayman Islands
  'lr', // Liberia
  'mm', // Myanmar
  'mh', // Marshall Islands
  'fm', // Micronesia
  'pw', // Palau
  'pr', // Puerto Rico
]);

/**
 * Extract region/country code from a BCP-47 locale string.
 * @param {string} locale - e.g., 'en-US', 'en_US', 'fr-FR', 'en-GB'
 * @returns {string|null} lowercase two-letter region, or null if unparseable
 */
function regionFromLocale(locale) {
  if (!locale || typeof locale !== 'string') return null;
  // Handle both 'en-US' and 'en_US' formats
  const parts = locale.replace('_', '-').split('-');
  // Region is the second or third segment depending on script/variant
  for (let i = 1; i < parts.length; i++) {
    const p = parts[i].trim();
    // Regions are 2-letter uppercase (US, GB, FR) or 3-digit numeric (419)
    if (/^[A-Za-z]{2}$/.test(p)) return p.toLowerCase();
  }
  return null;
}

/**
 * Resolve the temperature unit ('C' or 'F') to use for display.
 *
 * @param {Object} args
 * @param {string} [args.userPref]  - Explicit user preference, 'C' or 'F'. Wins if set.
 * @param {string} [args.locale]    - BCP-47 locale like 'en-US'. Used when userPref is absent.
 * @returns {'C'|'F'}
 */
export function resolveTemperatureUnit({ userPref, locale } = {}) {
  if (userPref === 'C' || userPref === 'F') return userPref;
  const region = regionFromLocale(locale);
  if (region && FAHRENHEIT_LOCALES.has(region)) return 'F';
  return 'C';
}

/**
 * Convert Celsius to Fahrenheit.
 * @param {number} c
 * @returns {number}
 */
export function celsiusToFahrenheit(c) {
  return c * 9 / 5 + 32;
}

/**
 * Format a temperature value for display.
 *
 * @param {number} celsius - Input value in Celsius (API native)
 * @param {Object} [options]
 * @param {'C'|'F'} [options.unit='C']  - Target display unit
 * @param {string} [options.suffix]     - Override suffix. Defaults to '°C' / '°F'.
 *                                         Pass '°' to omit the letter entirely.
 * @returns {string} Formatted temperature, e.g., '46°F' or '8°C'
 */
export function formatTemperature(celsius, options = {}) {
  if (celsius === null || celsius === undefined || Number.isNaN(celsius)) return '—';
  const { unit = 'C', suffix } = options;
  const value = unit === 'F' ? celsiusToFahrenheit(celsius) : celsius;
  const rounded = Math.round(value);
  const unitSuffix = suffix !== undefined ? suffix : `°${unit}`;
  return `${rounded}${unitSuffix}`;
}

/**
 * Format a hi/lo temperature range for compact display.
 * Appends the unit only once at the end, e.g. "46°/27°F" or "8°/-3°C".
 *
 * @param {number} highC - High temp in Celsius
 * @param {number} lowC  - Low temp in Celsius
 * @param {Object} [options]
 * @param {'C'|'F'} [options.unit='C']
 * @returns {string}
 */
export function formatTemperatureRange(highC, lowC, options = {}) {
  const { unit = 'C' } = options;
  const hi = formatTemperature(highC, { unit, suffix: '°' });
  const lo = formatTemperature(lowC, { unit });
  return `${hi}/${lo}`;
}

export default {
  resolveTemperatureUnit,
  celsiusToFahrenheit,
  formatTemperature,
  formatTemperatureRange,
};
